import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Calendar, CheckSquare, FileText, Users,
  Bot, Share2, BarChart3, Receipt, Rss, ArrowRight,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api, type Agent } from '@/lib/api'
import { cn } from '@/lib/utils'

const nav = [
  { to: '/',           label: 'Dashboard',      icon: LayoutDashboard },
  { to: '/calendar',   label: 'Calendar',       icon: Calendar },
  { to: '/approvals',  label: 'Approvals',      icon: CheckSquare, badgeKey: 'pending' as const },
  { to: '/briefs',     label: 'Content briefs', icon: FileText },
  { to: '/writers',    label: 'Writers',        icon: Users },
  { to: '/agents',     label: 'Agents',         icon: Bot },
  { to: '/social',     label: 'Social',         icon: Share2 },
  { to: '/analytics',  label: 'Analytics',      icon: BarChart3 },
  { to: '/invoicing',  label: 'Invoicing',      icon: Receipt },
  { to: '/rss',        label: 'RSS feeds',      icon: Rss },
]

const agentStatusDot = {
  idle:    'bg-status-approved-accent',
  working: 'bg-status-review-accent',
  blocked: 'bg-status-danger-accent',
} as const

interface BlockReason {
  summary: string
  created_at: string
  related_article_id: string | null
  article_title: string | null
}

function BlockedAgentLink({ agent }: { agent: Agent }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const { data } = useQuery({
    queryKey: ['agent.block-reason', agent.id],
    queryFn: () => api.agentBlockReason(agent.id),
    enabled: open,
  })

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const [claim, reason] = parseBlockSummary(data ?? null)

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-xs md:text-[11px] text-status-danger-accent hover:underline py-0.5"
      >
        blocked
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-72 bg-surface-card border border-border rounded-md shadow-lg p-3 text-left">
          {!data ? (
            <div className="text-[11px] text-ink-tertiary">Loading…</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] text-ink-tertiary uppercase tracking-wider">Blocked on</div>
              <div className="text-xs font-medium text-ink-primary leading-snug">
                {data.article_title ?? 'Unknown article'}
              </div>
              {claim && (
                <>
                  <div className="text-[11px] text-ink-tertiary uppercase tracking-wider mt-1">Flagged claim</div>
                  <div className="text-xs italic text-ink-secondary leading-snug">&ldquo;{claim}&rdquo;</div>
                </>
              )}
              {reason && (
                <>
                  <div className="text-[11px] text-ink-tertiary uppercase tracking-wider mt-1">Reason</div>
                  <div className="text-xs text-ink-secondary leading-snug">{reason}</div>
                </>
              )}
              {data.related_article_id && (
                <button
                  onClick={() => {
                    setOpen(false)
                    navigate(`/approvals?highlight=${data.related_article_id}`)
                  }}
                  className="mt-1 text-[11px] text-brand-600 hover:underline flex items-center gap-0.5 self-start"
                >
                  View article <ArrowRight size={10} />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function parseBlockSummary(row: BlockReason | null): [string, string] {
  if (!row) return ['', '']
  const [claim, reason] = row.summary.split('|')
  return [claim?.trim() ?? '', reason?.trim() ?? '']
}

/**
 * Inner content of the sidebar — the nav list and the agents block.
 * Rendered both inside the desktop aside and inside the mobile drawer.
 * `onNavigate` is called after any nav item click so the drawer can close.
 */
export function SidebarContent({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { data: kpis } = useQuery({ queryKey: ['kpis'], queryFn: api.kpis })
  const { data: agents } = useQuery({ queryKey: ['agents'], queryFn: api.agents })

  return (
    <nav className="flex-1 overflow-auto px-2 pb-4">
      <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-ink-tertiary">Workspace</div>
      <ul className="flex flex-col gap-0.5">
        {nav.map(({ to, label, icon: Icon, badgeKey }) => {
          const badge =
            badgeKey === 'pending' && kpis?.pending_approvals ? kpis.pending_approvals : undefined
          return (
            <li key={to}>
              <NavLink
                to={to}
                end={to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 px-2 py-2 md:py-1.5 rounded-md text-sm',
                    isActive
                      ? 'bg-surface-muted text-ink-primary font-medium'
                      : 'text-ink-secondary hover:bg-surface-muted/60'
                  )
                }
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{label}</span>
                {badge !== undefined && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-status-danger-bg text-status-danger-fg">
                    {badge}
                  </span>
                )}
              </NavLink>
            </li>
          )
        })}
      </ul>

      <div className="px-2 pt-6 pb-2 text-[10px] uppercase tracking-wider text-ink-tertiary">Agents</div>
      <ul className="flex flex-col gap-2 px-2">
        {agents?.map((a) => (
          <li key={a.id} className="flex items-center justify-between text-xs gap-2">
            <NavLink
              to={`/agents/${a.id}/chat`}
              onClick={onNavigate}
              className="flex items-center gap-2 text-ink-secondary hover:text-ink-primary min-w-0 flex-1"
            >
              <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', agentStatusDot[a.status])} />
              <span className="truncate">{a.name}</span>
            </NavLink>
            {a.status === 'blocked' ? (
              <BlockedAgentLink agent={a} />
            ) : (
              <span className="text-xs md:text-[11px] text-ink-tertiary shrink-0">{a.status}</span>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}

export function Sidebar() {
  return (
    <aside className="hidden md:flex w-[200px] shrink-0 border-r border-border bg-surface-card flex-col">
      <div className="flex items-center gap-2 px-3 py-4">
        <img
          src="/logo.png"
          alt="Deadreckoning"
          className="h-6 w-auto object-contain"
        />
        <div className="text-sm font-medium leading-tight">Deadreckoning</div>
      </div>
      <SidebarContent />
    </aside>
  )
}
